/**
 * Skeleton loading screen for the standalone (full-page) conversation layout.
 *
 * Uses `StandaloneLayout` to replicate the two-column desktop grid and
 * single-column mobile layout without duplicating layout logic. The nav bar,
 * header placeholder, left-rail comment list, and right-rail cards are each
 * filled with skeleton primitives from `component/skeleton`.
 */

import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { Pad, Separator, VisuallyHidden, WindowTitle } from 'component/basics';
import { spacings } from 'component/constants';
import { BaseSkeleton } from 'component/progress/base-skeleton';
import { BylineSkeleton } from 'component/progress/byline-skeleton';
import { CommentSkeleton } from 'component/progress/comment-skeleton';
import { PromptSkeleton } from 'component/progress/prompt-skeleton';
import { SkeletonNavBar } from 'component/progress/skeleton-nav-bar';
import { StoryCardSkeleton } from 'component/progress/story-card-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { UtilityText } from 'component/text';
import { View } from 'react-native';
import { useIsDesktopBreakpoint } from 'util/layout';
import { t } from 'util/stdtypes';

import { StandaloneLayout } from './standalone-layout';

// Nav bar shows skeleton placeholders rather than the real SiteLogo/UserInfo:
// those depend on the datastore, so gating them on it flashed a logged-out
// state for logged-in users on slow networks before the data arrived.
function SkeletonStandaloneNavBar({ large }: { large: boolean }) {
    return (
        <SkeletonNavBar paddingHorizontal={large ? spacings.xl4 : spacings.lg}>
            <TextContainerSkeleton borderRadius={4} height={24} width={100} />
            <BaseSkeleton borderRadius={16} height={32} width={32} />
        </SkeletonNavBar>
    );
}

function SkeletonEditorialCard() {
    const s = useStyles();
    return (
        <View style={s.editorialCard}>
            <TextContainerSkeleton borderRadius={2} height={6} width={140} />
            <Pad size={spacings.xl2} />
            <View style={s.bylineItem}>
                <BylineSkeleton />
            </View>
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <View style={s.bylineItem}>
                <BylineSkeleton />
            </View>
        </View>
    );
}

function SkeletonRelatedContentCard() {
    const s = useStyles();
    return (
        <View style={s.relatedContentCard}>
            <TextContainerSkeleton borderRadius={2} height={6} width={140} />
            <Pad size={spacings.xl2} />
            <StoryCardSkeleton />
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <StoryCardSkeleton />
            <Pad size={spacings.lg} />
            <Separator />
            <Pad size={spacings.lg} />
            <StoryCardSkeleton />
        </View>
    );
}

export function SkeletonStandaloneConversationScreen() {
    const isDesktop = useIsDesktopBreakpoint();
    const s = useStyles();
    const { colors } = useTheme();

    return (
        <View style={s.outer}>
            <WindowTitle label={t('loading', 'Loading...')} />
            <VisuallyHidden aria-live='polite' role='status'>
                <UtilityText label={t('loading', 'Loading...')} />
            </VisuallyHidden>
            <SkeletonStandaloneNavBar large={isDesktop} />
            <StandaloneLayout
                header={<View style={[s.headerPlaceholder, { height: isDesktop ? 110 : 188 }]} />}
                headerBackgroundColor={colors.colorSurfaceSecondary}
                rightRail={
                    <>
                        <SkeletonEditorialCard />
                        <Pad size={spacings.xl2} />
                        <SkeletonRelatedContentCard />
                    </>
                }
            >
                <PromptSkeleton />
                <View style={s.sortRow}>
                    <TextContainerSkeleton borderRadius={2} height={6} width={60} />
                </View>
                {Array.from({ length: isDesktop ? 4 : 3 }).map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <CommentSkeleton key={i} variant={isDesktop ? 'desktop' : 'mobile'} />
                ))}
            </StandaloneLayout>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
    headerPlaceholder: { width: '100%', backgroundColor: tokens.colors.colorSurfaceSecondary },
    sortRow: { paddingHorizontal: 20, paddingVertical: 16 },
    editorialCard: {
        borderWidth: 1,
        borderColor: tokens.colors.colorBorderPrimary,
        borderRadius: spacings.sm,
        padding: spacings.xl2,
        backgroundColor: tokens.colors.colorSurfacePrimary,
    },
    bylineItem: { paddingHorizontal: spacings.lg },
    relatedContentCard: {
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: spacings.sm,
        padding: spacings.xl2,
    },
}));
